import {createMeasurementHooks} from './measurement-hooks.js';
import {createCameraStallGuard} from './camera-stall-guard.js';
import {buildRuntimeConfig} from './runtime-config.js';

const ENGINE_ASSET_PATH = '/ar-runtime/0.1.0/';
const TARGET_LOAD_CONCURRENCY = 6;
const CAMERA_STARTUP_EVENT_LIMIT = 24;
const TEST_HOOKS_AVAILABLE_IN_BUILD = import.meta.env?.VITE_BROWSER_AR_TEST_HOOKS === 'true';

/**
 * Keep the camera and image-tracking lifecycle outside Elm's update loop.
 * Elm sends coarse commands and receives coarse state changes only.
 */
export function connectArPorts(app, {
  documentRef = globalThis.document,
  windowRef = globalThis.window,
  fetchImpl = globalThis.fetch?.bind(globalThis),
  runtime = null,
} = {}) {
  // Warm these independent AR chunks as soon as the Elm app connects, but let
  // the details page render while the larger runtime is still downloading.
  // Capture import failures as values so a user who never enters AR does not
  // trigger an unhandled promise rejection.
  const runtimeImport = runtime
    ? Promise.resolve({module: runtime})
    : import('@argyle/browser-ar-runtime').then(module => ({module}), error => ({error}));
  const presentationImport = import('./video-presentation.js')
    .then(module => ({module}), error => ({error}));
  const enableTestHooks = TEST_HOOKS_AVAILABLE_IN_BUILD;
  if (enableTestHooks) windowRef.__browserArTestFlags = {enabled: true};
  const diagnosticsEnabled = diagnosticsRequested(windowRef);
  let lastCameraStartup = null;
  let active = null;
  let generation = 0;
  let diagnosticsState = 'idle';
  let diagnosticsSessionGeneration = 0;
  let diagnosticsSessionStartedAt = null;
  let diagnosticsSessionRunningAt = null;
  const measurements = createMeasurementHooks(windowRef.performance);
  let currentForDiagnostics = null;

  if (diagnosticsEnabled) {
    const diagnostics = {enabled: true};
    Object.defineProperties(diagnostics, {
      state: {enumerable: true, get: () => diagnosticsState},
      sessionGeneration: {enumerable: true, get: () => diagnosticsSessionGeneration},
      sessionStartedAt: {enumerable: true, get: () => diagnosticsSessionStartedAt},
      sessionRunningAt: {enumerable: true, get: () => diagnosticsSessionRunningAt},
      platform: {enumerable: true, get: () => detectBrowserPlatform(windowRef.navigator)},
      targetSetReady: {enumerable: true, get: () => Boolean(currentForDiagnostics?.targetsReady)},
      metrics: {enumerable: true, get: () => measurements.snapshot()},
      cameraStartup: {enumerable: true, get: () => currentForDiagnostics?.cameraStartup ?? lastCameraStartup},
      cameraStalls: {enumerable: true, get: () => currentForDiagnostics?.cameraStallGuard?.events ?? []},
      ...(enableTestHooks ? {syntheticCameraEvents: {
        enumerable: true,
        get: () => currentForDiagnostics?.cameraStatusEvents ?? [],
      }} : {}),
    });
    diagnostics.resetPoseWindow = () => measurements.resetPoseWindow();
    diagnostics.markStationary = () => measurements.markStationary();
    windowRef.__browserArDiagnostics = diagnostics;
  }

  function publishDiagnostics(nextState) {
    if (!diagnosticsEnabled) return;
    if (nextState !== undefined) diagnosticsState = nextState;
  }

  const emit = value => app.ports.arEvents.send(value);
  emit({
    type: 'environment',
    platform: enableTestHooks ? 'android' : (detectBrowserPlatform(windowRef.navigator) ?? 'unsupported'),
    embeddedBrowser: isEmbeddedBrowser(windowRef.navigator),
  });
  function stopCurrent() {
    const closing = active;
    active = null;
    if (currentForDiagnostics === closing) currentForDiagnostics = null;
    if (closing) closing.closed = true;
    if (closing) {
      // Freeze the final media counters before stop() removes each src and
      // resets the video element's dimensions, time and decoded-frame stats.
      measurements.unwatchVideos();
      closing.presentation?.stop();
      closing.testMediaCleanup?.();
      if (closing.canvasContextLost) closing.canvas.removeEventListener?.('webglcontextlost', closing.canvasContextLost);
      if (closing.slowStartTimer !== null) clearTimeout(closing.slowStartTimer);
      if (!closing.failed) closing.runtimeSession?.stop();
      if (enableTestHooks && windowRef.__browserArTest === closing.testApi) delete windowRef.__browserArTest;
    }
    diagnosticsState = 'stopped';
  }

  function devicePlatform() {
    return enableTestHooks ? 'android' : detectBrowserPlatform(windowRef.navigator);
  }

  async function prepare(experience) {
    const requestGeneration = ++generation;
    stopCurrent();
    diagnosticsState = 'preparing';

    try {
      const platform = devicePlatform();
      if (!platform) throw new Error('Open this experience on an iPhone, iPad, or Android phone.');
      const config = buildRuntimeConfig(experience, platform);

      emit({type: 'progress', message: 'Loading the AR viewer…', fraction: 0.2});
      const runtimeResult = await runtimeImport;
      if (runtimeResult.error) throw runtimeResult.error;
      const runtimeApi = runtimeResult.module;
      assertPlayableVideos(config.videos, documentRef, runtimeApi.UnsupportedMediaError);
      const engine = await runtimeApi.loadEngine({assetBaseUrl: ENGINE_ASSET_PATH});
      if (requestGeneration !== generation) return;

      emit({type: 'progress', message: 'Preparing image recognition…', fraction: 0.5});
      let loadedTargetCount = 0;
      // The original anchor images become the targets. Each target is named by
      // its anchor ID, which the reality.image* events report back.
      const targets = await mapConcurrent(config.targets, TARGET_LOAD_CONCURRENCY, async target => {
        const loaded = await runtimeApi.loadImageTarget({name: target.id, imageUrl: target.imageUrl, contentType: target.imageContentType});
        loadedTargetCount += 1;
        if (requestGeneration === generation) {
          emit({type: 'progress', message: 'Preparing image recognition…', fraction: 0.5 + 0.45 * loadedTargetCount / config.targets.length});
        }
        return loaded;
      });
      if (requestGeneration !== generation) return;

      const canvas = await waitForCanvas(
        documentRef,
        windowRef,
        () => requestGeneration === generation,
      );
      if (requestGeneration !== generation) return;
      if (!canvas) throw new Error('The AR view is not mounted. Return to experience details and try again.');

      const current = {
        canvas,
        windowRef,
        fetchImpl,
        runtimeSession: null,
        runtimeApi,
        measurements,
        config,
        loadedTargets: targets,
        sceneContext: null,
        presentation: null,
        presentationReady: Promise.resolve(),
        testMediaCleanup: null,
        testApi: null,
        trackedDetails: new Map(),
        soundMuted: true,
        pendingVideoSources: null,
        canvasContextLost: null,
        slowStartTimer: null,
        cameraStatusEvents: [],
        cameraStream: null,
        cameraStartup: null,
        cameraStartupStartedAt: null,
        cameraStartupFrozen: false,
        cameraVideoSizeSeen: false,
        cameraUpdateSeen: false,
        tracked: new Set(),
        targetsReady: false,
        started: false,
        publishDiagnostics,
      };
      current.canvasContextLost = event => {
        event.preventDefault?.();
        fail(current, new Error('The AR graphics session was interrupted. Close the camera and try again.'), emit);
      };
      canvas.addEventListener?.('webglcontextlost', current.canvasContextLost);
      current.cameraStallGuard = createCameraStallGuard({
        now: () => startupClock(current),
        cameraVideo: () => cameraVideoFor(documentRef, current.cameraStreamForRecovery),
        cameraTrack: () => current.cameraStreamForRecovery?.getVideoTracks?.()[0] ?? null,
        isHidden: () => Boolean(documentRef.hidden),
      });
      currentForDiagnostics = current;
      current.runtimeSession = runtimeApi.createImageTrackingSession({
        XR8: engine,
        canvas,
        targets,
        callbacks: {
          onTargetFound: detail => onTarget(current, detail, true, emit),
          onTargetUpdated: detail => onTarget(current, detail, true, emit),
          onTargetLost: detail => onTarget(current, detail, false, emit),
          onSceneReady: context => {
            recordCameraStartup(current, 'scene-ready');
            current.sceneContext = context;
            current.presentationReady = setupPresentation(current, emit, windowRef, documentRef, presentationImport, diagnosticsEnabled)
              .catch(error => fail(current, error, emit));
          },
          onTargetsReady: () => {
            current.targetsReady = true;
          },
          onUpdate: input => {
            measurements.update(input);
            current.cameraStallGuard.update(input?.frameStartResult, current.tracked.size > 0);
            if (!current.cameraUpdateSeen) {
              current.cameraUpdateSeen = true;
              recordCameraStartup(current, 'first-xr-update');
              sampleCameraStartup(current, documentRef);
            }
          },
          onVideoSizeChange: dimensions => {
            measurements.cameraResolution(dimensions);
            if (!current.cameraVideoSizeSeen) {
              current.cameraVideoSizeSeen = true;
              recordCameraStartup(current, 'camera-video-size', {
                widthPx: safeDimension(dimensions?.videoWidth),
                heightPx: safeDimension(dimensions?.videoHeight),
              });
            }
            sampleCameraStartup(current, documentRef);
          },
          onError: error => fail(current, error, emit),
          onCameraStatusChange: event => {
            const status = safeCameraStatus(event?.status);
            const reason = safeCameraReason(event?.reason);
            if (status) recordCameraStartup(current, `camera-${status}`, reason ? {reason} : undefined);
            if (enableTestHooks) {
              current.cameraStatusEvents.push({status: event?.status ?? null, reason: event?.reason ?? null});
            }
            if (event?.status === 'hasStream') {
              current.cameraStreamForRecovery = event.stream ?? null;
              current.cameraStream = diagnosticsEnabled ? (event.stream ?? null) : null;
              if (current.cameraStartup) {
                current.cameraStartup.camera.tracksOnStream = safeCameraTracks(current.cameraStream);
                sampleCameraStartup(current, documentRef);
              }
              measurements.cameraReady();
              if (diagnosticsEnabled) measurements.observeCameraStream(event?.stream);
            }
            if (event?.status === 'failed') fail(current, cameraStatusFailure(event), emit);
          },
          onBeforeStop: detail => finishCameraStartup(current, documentRef, detail?.reason),
        },
      });

      if (requestGeneration !== generation) {
        current.runtimeSession.stop();
        return;
      }
      active = current;
      diagnosticsState = 'prepared';
      emit({type: 'prepared'});
    } catch (error) {
      if (requestGeneration !== generation) return;
      diagnosticsState = 'failed';
      reportFailure(error, emit);
    }
  }

  function start() {
    const current = active;
    if (!current) {
      emit({type: 'error', message: 'The AR session is no longer available. Close and try again.'});
      return;
    }
    if (current.started) return;

    try {
      diagnosticsSessionGeneration++;
      diagnosticsSessionStartedAt = new Date().toISOString();
      diagnosticsSessionRunningAt = null;
      current.canvas.width = Math.round(windowRef.innerWidth * pixelRatio(windowRef));
      current.canvas.height = Math.round(windowRef.innerHeight * pixelRatio(windowRef));
      current.started = true;
      diagnosticsState = 'starting';
      if (diagnosticsEnabled) {
        current.cameraStartup = {
          schemaVersion: 1,
          state: 'starting',
          startedAt: new Date().toISOString(),
          finishedAt: null,
          failureStage: null,
          events: [],
          camera: {tracksOnStream: [], lastSample: null},
        };
        current.cameraStartupStartedAt = startupClock(current);
        current.cameraStartupFrozen = false;
        current.cameraVideoSizeSeen = false;
        current.cameraUpdateSeen = false;
        lastCameraStartup = current.cameraStartup;
        recordCameraStartup(current, 'start-requested');
      }
      measurements.cameraStart();
      current.presentation?.start();
      // This call stays in the browser event handler, preserving camera
      // permission and media activation requirements on mobile Safari.
      current.runtimeSession.start().then(
        () => {
          recordCameraStartup(current, 'runtime-ready');
          finishCameraStartup(current, documentRef, 'ready');
          diagnosticsSessionRunningAt = new Date().toISOString();
          diagnosticsState = 'running';
          emit({type: 'started', message: 'Camera ready. Point at the printed image.'});
        },
        error => fail(current, error, emit),
      );
    } catch (error) {
      fail(current, error, emit);
    }
  }

  function handleVideoAction(event) {
    const action = event.target?.closest?.('[data-ar-video-action]')?.getAttribute?.('data-ar-video-action');
    const current = active;
    if (!current?.started || !action) return;
    if (action === 'sound') {
      current.soundMuted = !current.soundMuted;
      if (current.presentation) current.presentation.setMuted(current.soundMuted);
      else emit({type: 'sound', muted: current.soundMuted});
    } else if (action === 'retry') {
      const videoId = event.target?.closest?.('[data-ar-video-action="retry"]')?.getAttribute?.('data-video-id') ?? null;
      current.presentation?.retryPlayback(videoId);
    }
  }

  /**
   * Swap in freshly signed video URLs after Retry video fetched the experience
   * again. If the retried video can no longer be refreshed for this device,
   * its error and Retry return instead of disappearing.
   */
  function refreshMedia({videoId, experience} = {}) {
    const current = active;
    if (!current) return;
    let sources;
    try {
      sources = new Map(buildRuntimeConfig(experience, devicePlatform()).videos.map(video => [video.id, video.url]));
    } catch (error) {
      console.warn('Browser AR could not refresh video sources:', error?.message);
      sources = new Map();
    }
    if (!sources.has(videoId)) {
      emit({type: 'media-error', videoId, message: VIDEO_REFRESH_FAILED_MESSAGE});
      return;
    }
    if (current.presentation) void current.presentation.updateVideoSources(sources);
    else current.pendingVideoSources = sources;
  }

  app.ports.prepareAr.subscribe(prepare);
  app.ports.startAr.subscribe(start);
  app.ports.refreshArMedia.subscribe(refreshMedia);
  app.ports.stopAr.subscribe(() => {
    generation++;
    stopCurrent();
  });

  const resize = () => {
    if (active?.started) {
      active.canvas.width = Math.round(windowRef.innerWidth * pixelRatio(windowRef));
      active.canvas.height = Math.round(windowRef.innerHeight * pixelRatio(windowRef));
    }
  };
  const visibility = () => {
    if (!active?.started) return;
    if (documentRef.hidden) {
      active.runtimeSession.pause();
      active.presentation?.pause();
      clearTracking(active, emit);
      diagnosticsState = 'paused';
    } else {
      active.runtimeSession.resume();
      active.presentation?.resume();
      diagnosticsState = 'running';
    }
  };
  const pageHide = () => {
    generation++;
    stopCurrent();
  };
  const pageShow = event => {
    if (event.persisted) windowRef.location.reload();
  };

  documentRef.addEventListener('visibilitychange', visibility);
  documentRef.addEventListener('click', handleVideoAction, true);
  windowRef.addEventListener('resize', resize);
  windowRef.addEventListener('orientationchange', resize);
  windowRef.addEventListener('pagehide', pageHide);
  windowRef.addEventListener('pageshow', pageShow);

  return {
    snapshotMeasurements() { return measurements.snapshot(); },
    diagnosticsEnabled,
    observePresentationVideo(video) {
      measurements.watchVideo(video);
    },
    observePresentationVideos(videos) {
      measurements.watchVideos(videos);
    },
    stopObservingPresentationVideo() {
      measurements.unwatchVideo();
    },
    stopObservingPresentationVideos() {
      measurements.unwatchVideos();
    },
    dispose() {
      generation++;
      stopCurrent();
      documentRef.removeEventListener('visibilitychange', visibility);
      documentRef.removeEventListener('click', handleVideoAction, true);
      windowRef.removeEventListener('resize', resize);
      windowRef.removeEventListener('orientationchange', resize);
      windowRef.removeEventListener('pagehide', pageHide);
      windowRef.removeEventListener('pageshow', pageShow);
    },
  };
}

/**
 * Refuse a video type this browser reports it cannot play, before the camera
 * starts. QuickTime shares MP4's ISO-BMFF container, and H.264 MOV files play
 * in Chrome and Safari, so a MOV is checked as MP4.
 */
function assertPlayableVideos(videos, documentRef, UnsupportedMediaError) {
  const probe = documentRef.createElement('video');
  if (typeof probe.canPlayType !== 'function') return;
  for (const video of videos) {
    const [mediaType, ...parameters] = video.contentType.split(';');
    const probeType = mediaType.trim().toLowerCase() === 'video/quicktime'
      ? ['video/mp4', ...parameters].join(';')
      : video.contentType;
    if (probe.canPlayType(probeType) === '') {
      throw new UnsupportedMediaError(`Video ${video.id} has a type this browser cannot play (${video.contentType}).`);
    }
  }
}

const MEDIA_ERR_DECODE = 3;
const MEDIA_ERR_SRC_NOT_SUPPORTED = 4;

/**
 * Whether a failed video is a format this browser cannot play, rather than a
 * load failure that fresh URLs could fix. A decode error is always the format.
 * Chromium also reports an HTTP 403/404 or a refused connection as
 * MEDIA_ERR_SRC_NOT_SUPPORTED, so that code (and play()'s NotSupportedError)
 * counts as unsupported only when the source itself is reachable.
 */
export async function isUnsupportedVideoFailure(error, url, fetchImpl) {
  if (error?.code === MEDIA_ERR_DECODE) return true;
  if (error?.code !== MEDIA_ERR_SRC_NOT_SUPPORTED && error?.name !== 'NotSupportedError') return false;
  if (!url || typeof fetchImpl !== 'function') return false;
  const controller = new AbortController();
  try {
    const response = await fetchImpl(url, {mode: 'cors', credentials: 'omit', cache: 'no-store', signal: controller.signal});
    return response.ok;
  } catch {
    return false;
  } finally {
    // Only the status matters; stop the body download.
    controller.abort();
  }
}

export function detectBrowserPlatform(navigatorRef = globalThis.navigator) {
  const userAgent = navigatorRef?.userAgent ?? '';
  const platform = navigatorRef?.userAgentData?.platform ?? navigatorRef?.platform ?? '';
  if (/iPhone|iPad|iPod/i.test(userAgent) || (/Mac/i.test(platform) && (navigatorRef?.maxTouchPoints ?? 0) > 1)) return 'ios';
  if (/Android/i.test(userAgent) || /Android/i.test(platform)) return 'android';
  return null;
}

export function isEmbeddedBrowser(navigatorRef = globalThis.navigator) {
  const userAgent = navigatorRef?.userAgent ?? '';
  return /;\s*wv\)|\bwv\b|fban|fbav|instagram|line\/|tiktok|snapchat|micromessenger|webview/i.test(userAgent);
}

function cameraBlocked(name, normalizedMessage) {
  return name === 'NotAllowedError' || name === 'PermissionDeniedError' || /camera access failed|permission (?:was )?denied|not allowed to access (?:the )?camera/.test(normalizedMessage);
}

function networkFailure(normalizedMessage) {
  return /failed to fetch|networkerror|network request failed|load failed|connection|timeout/i.test(normalizedMessage);
}

export function recoveryReason(error) {
  const normalizedMessage = safeMessage(error).toLowerCase();
  if (error?.name === 'UnsupportedMediaError') return 'unsupported-media';
  if (error?.name === 'MediaLoadError') return 'network';
  if (cameraBlocked(error?.name ?? '', normalizedMessage)) return 'camera-blocked';
  if (networkFailure(normalizedMessage)) return 'network';
  return 'other';
}

export function recoveryMessage(error) {
  const name = error?.name ?? '';
  const rawMessage = safeMessage(error);
  const normalizedMessage = rawMessage.toLowerCase();
  if (name === 'UnsupportedMediaError') return UNSUPPORTED_MEDIA_MESSAGE;
  if (name === 'MediaLoadError') {
    return 'An image for this experience could not load. Check your connection and try again.';
  }
  if (cameraBlocked(name, normalizedMessage)) {
    return 'Camera access is blocked. Allow camera access in your browser site settings, then try again. If this link opened inside another app, use Open in browser.';
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return 'No compatible camera is available. Close other camera apps and try again on a supported phone.';
  }
  if (name === 'NotReadableError' || name === 'AbortError') {
    return 'The camera is busy or could not start. Close other camera apps, then try again.';
  }
  if (networkFailure(normalizedMessage)) {
    return 'The AR viewer could not load its image data. Check your connection and try again.';
  }
  return rawMessage;
}

function diagnosticsRequested(windowRef) {
  try {
    return new URLSearchParams(windowRef.location?.search ?? '').get('arDiagnostics') === '1';
  } catch {
    return false;
  }
}

function startupClock(current) {
  const now = current.windowRef.performance?.now?.();
  return Number.isFinite(now) ? now : Date.now();
}

function recordCameraStartup(current, stage, details = undefined) {
  const diagnostic = current.cameraStartup;
  if (!diagnostic || current.cameraStartupFrozen) return;
  const elapsed = Math.max(0, startupClock(current) - current.cameraStartupStartedAt);
  diagnostic.events.push({
    stage,
    at: new Date().toISOString(),
    elapsedMs: Math.round(elapsed),
    ...(details ? {details} : {}),
  });
  if (diagnostic.events.length > CAMERA_STARTUP_EVENT_LIMIT) {
    diagnostic.events.splice(0, diagnostic.events.length - CAMERA_STARTUP_EVENT_LIMIT);
  }
}

function safeDimension(value) {
  return Number.isFinite(value) && value > 0 ? Math.round(value) : null;
}

function safeCameraStatus(status) {
  return ['requesting', 'hasStream', 'hasVideo', 'failed'].includes(status) ? status : null;
}

function safeCameraReason(reason) {
  return ['DENY_CAMERA', 'NO_CAMERA', 'UNSPECIFIED'].includes(reason) ? reason : null;
}

// The camera element XR8 created for this stream, if it is in the document.
function cameraVideoFor(documentRef, stream) {
  if (!stream || typeof documentRef.querySelectorAll !== 'function') return null;
  return [...documentRef.querySelectorAll('video')].find(candidate => candidate.srcObject === stream) ?? null;
}

function safeCameraTracks(stream) {
  try {
    return (stream?.getVideoTracks?.() ?? []).map(track => ({
      kind: track.kind === 'video' ? 'video' : 'other',
      enabled: typeof track.enabled === 'boolean' ? track.enabled : null,
      muted: typeof track.muted === 'boolean' ? track.muted : null,
      readyState: ['live', 'ended'].includes(track.readyState) ? track.readyState : null,
    }));
  } catch {
    return [];
  }
}

function safeCameraVideo(documentRef, windowRef, stream) {
  if (!stream || typeof documentRef.querySelectorAll !== 'function') return null;
  let video;
  try {
    video = [...documentRef.querySelectorAll('video')].find(candidate => candidate.srcObject === stream);
  } catch {
    return null;
  }
  if (!video) return null;

  let rect = null;
  let inViewport = null;
  try {
    const bounds = video.getBoundingClientRect();
    const left = Number(bounds.left ?? bounds.x);
    const top = Number(bounds.top ?? bounds.y);
    const width = Number(bounds.width);
    const height = Number(bounds.height);
    if ([left, top, width, height].every(Number.isFinite)) {
      rect = {left: roundPixel(left), top: roundPixel(top), width: roundPixel(width), height: roundPixel(height)};
      inViewport = left < windowRef.innerWidth && top < windowRef.innerHeight && left + width > 0 && top + height > 0;
    }
  } catch { /* video geometry is optional diagnostic data */ }

  let style = null;
  try {
    const computed = windowRef.getComputedStyle?.(video) ?? globalThis.getComputedStyle?.(video);
    if (computed) {
      style = {
        display: ['none', 'block', 'inline', 'inline-block', 'contents'].includes(computed.display) ? computed.display : 'other',
        visibility: ['visible', 'hidden', 'collapse'].includes(computed.visibility) ? computed.visibility : 'other',
        opacity: Number.isFinite(Number(computed.opacity)) ? Number(computed.opacity) : null,
      };
    }
  } catch { /* computed styles are optional diagnostic data */ }

  return {
    connected: Boolean(video.isConnected),
    paused: typeof video.paused === 'boolean' ? video.paused : null,
    readyState: Number.isFinite(video.readyState) ? video.readyState : null,
    currentTimeSeconds: Number.isFinite(video.currentTime) ? Math.round(video.currentTime * 1000) / 1000 : null,
    widthPx: safeDimension(video.videoWidth),
    heightPx: safeDimension(video.videoHeight),
    rect,
    inViewport,
    style,
    documentHidden: Boolean(documentRef.hidden),
    visibilityState: ['visible', 'hidden', 'prerender'].includes(documentRef.visibilityState) ? documentRef.visibilityState : 'unknown',
  };
}

function roundPixel(value) {
  return Math.round(value * 10) / 10;
}

function sampleCameraStartup(current, documentRef) {
  const diagnostic = current.cameraStartup;
  if (!diagnostic || current.cameraStartupFrozen) return;
  diagnostic.camera.lastSample = {
    at: new Date().toISOString(),
    tracks: safeCameraTracks(current.cameraStream),
    video: safeCameraVideo(documentRef, current.windowRef, current.cameraStream),
  };
}

function cameraStartupFailureStage(diagnostic) {
  const stages = new Set(diagnostic.events.map(event => event.stage));
  if (!stages.has('camera-hasStream')) return 'camera stream was not delivered';
  const video = diagnostic.camera.lastSample?.video;
  if (!video) return 'XR8 camera video element was not available';
  if (!video.widthPx || !video.heightPx || video.readyState < 2 || !video.currentTimeSeconds) {
    return 'camera video did not deliver a decoded frame';
  }
  if (!stages.has('scene-ready')) return 'AR scene did not start after camera frames arrived';
  if (!stages.has('runtime-ready')) return 'XR8 startup did not finish after the scene started';
  return 'camera and scene reported ready before timeout';
}

function finishCameraStartup(current, documentRef, reason) {
  const diagnostic = current.cameraStartup;
  if (!diagnostic || current.cameraStartupFrozen) return;
  sampleCameraStartup(current, documentRef);
  if (reason !== 'ready') {
    recordCameraStartup(current, reason === 'startup-timeout' ? 'startup-timeout' : reason === 'stopped' ? 'session-stopped' : 'startup-failed');
  }
  if (reason === 'startup-timeout') diagnostic.failureStage = cameraStartupFailureStage(diagnostic);
  diagnostic.state = reason === 'ready' ? 'ready' : reason === 'stopped' ? 'stopped' : reason === 'startup-timeout' ? 'timed-out' : 'failed';
  diagnostic.finishedAt = new Date().toISOString();
  current.cameraStartupFrozen = true;
}

function onTarget(current, detail, found, emit) {
  if (current.closed || !current.started || !detail?.name) return;
  const wasTracked = current.tracked.has(detail.name);
  if (found) {
    current.tracked.add(detail.name);
    current.trackedDetails.set(detail.name, detail);
    current.presentation?.onTargetFound(detail);
    current.measurements.observeTargetPose(detail);
    if (!wasTracked) current.measurements.targetFound();
  } else {
    current.tracked.delete(detail.name);
    current.trackedDetails.delete(detail.name);
    current.presentation?.onTargetLost(detail.name);
    current.measurements.clearTargetPose(detail.name);
    if (wasTracked) current.measurements.targetLost();
  }
  const isTracking = current.tracked.size > 0;
  if (isTracking && !current.reportedTracking) emit({type: 'found'});
  if (!isTracking && current.reportedTracking) emit({type: 'lost'});
  current.reportedTracking = isTracking;
}

function clearTracking(current, emit) {
  if (current.tracked.size > 0) emit({type: 'lost'});
  for (const name of current.tracked) {
    current.presentation?.onTargetLost(name);
    current.measurements.clearTargetPose(name);
    current.measurements.targetLost();
  }
  current.tracked.clear();
  current.trackedDetails.clear();
  current.reportedTracking = false;
}

function fail(current, error, emit) {
  if (current.closed || current.failed) return;
  current.failed = true;
  current.publishDiagnostics?.('failed');
  current.started = false;
  current.measurements.unwatchVideos();
  current.presentation?.stop();
  current.testMediaCleanup?.();
  current.presentation = null;
  current.testMediaCleanup = null;
  current.runtimeSession?.stop();
  clearTracking(current, emit);
  reportFailure(error, emit);
}

function cameraStatusFailure(event) {
  if (event?.reason === 'DENY_CAMERA') {
    return Object.assign(new Error('Camera permission was denied.'), {name: 'NotAllowedError'});
  }
  if (event?.reason === 'NO_CAMERA') {
    return Object.assign(new Error('No compatible camera is available.'), {name: 'NotFoundError'});
  }
  return new Error('Camera startup failed. Close other camera apps and try again.');
}

// Visitor-facing media messages never include IDs or raw error text; details go to the console.
const UNSUPPORTED_MEDIA_MESSAGE = 'This experience uses an image or video this browser can’t show. Get the Immersify app to view it.';
const VIDEO_FAILED_MESSAGE = 'The video couldn’t load. Check your connection and tap Retry video, or get the Immersify app.';
const VIDEO_UNSUPPORTED_MESSAGE = 'This video can’t play in this browser. Get the Immersify app to view it.';
const VIDEO_REFRESH_FAILED_MESSAGE = 'The video couldn’t be refreshed. Check your connection and tap Retry video.';

function reportFailure(error, emit) {
  if (error?.name === 'UnsupportedMediaError' || error?.name === 'MediaLoadError') {
    console.warn('Browser AR media failure:', error.message);
  }
  emit({type: 'error', message: recoveryMessage(error), reason: recoveryReason(error)});
}

async function setupPresentation(current, emit, windowRef, documentRef, presentationImport, diagnosticsEnabled) {
  const reportVideoFailure = async (videoId, error, url) => {
    const unsupported = await isUnsupportedVideoFailure(error, url, current.fetchImpl);
    if (current.closed) return;
    if (unsupported) console.warn('Browser AR video cannot play in this browser:', error?.message ?? error?.code);
    emit({
      type: 'media-error',
      videoId,
      unsupported,
      message: unsupported ? VIDEO_UNSUPPORTED_MESSAGE : VIDEO_FAILED_MESSAGE,
    });
  };
  const enableTestHooks = TEST_HOOKS_AVAILABLE_IN_BUILD;
  const presentationResult = await presentationImport;
  if (presentationResult.error) throw presentationResult.error;
  const {createVideoPresentationManager} = presentationResult.module;
  const media = enableTestHooks ? await loadBrowserTestVideoFixture(documentRef, windowRef) : null;
  if (current.closed) {
    media?.cleanup();
    return;
  }

  current.presentation = createVideoPresentationManager({
    scene: current.sceneContext.scene,
    config: current.config,
    targetPropertiesById: new Map(current.loadedTargets.map(target => [target.name, target.properties])),
    documentRef,
    ...(media ? {videoFactory: () => media.video} : {}),
    initialMuted: current.soundMuted,
    onPlaybackBlocked: videoId => emit({
      type: 'playback-blocked',
      videoId,
      message: current.soundMuted
        ? 'Video playback was blocked. Tap Retry video to continue.'
        : 'Sound could not start. Tap Retry video or mute the video to continue.',
    }),
    onPlaybackError: (videoId, error, url) => reportVideoFailure(videoId, error, url),
    onMediaWaiting: videoId => emit({type: 'media-waiting', videoId}),
    onMediaUntracked: videoId => emit({type: 'media-untracked', videoId}),
    onMediaPlaying: videoId => emit({type: 'media-playing', videoId}),
    onMediaError: (videoId, error, url) => reportVideoFailure(videoId, error, url),
    onMediaRenewed: videoId => current.measurements.noteVideoRenewal(videoId),
    onSoundChange: state => emit({type: 'sound', muted: state.muted}),
  });
  if (media) current.testMediaCleanup = media.cleanup;
  if (diagnosticsEnabled) current.measurements.watchVideos(current.presentation.videoElements);
  if (current.started) current.presentation.start();
  for (const detail of current.trackedDetails.values()) current.presentation.onTargetFound(detail);
  if (current.pendingVideoSources) {
    const sources = current.pendingVideoSources;
    current.pendingVideoSources = null;
    await current.presentation.updateVideoSources(sources);
  }

  if (enableTestHooks) {
    const target = current.config.targets[0];
    const properties = current.loadedTargets.find(loaded => loaded.name === target.id).properties;
    const three = current.sceneContext.THREE;
    const syntheticDetail = {
      name: target.id,
      position: new three.Vector3(0, 0, -1),
      rotation: new three.Quaternion(),
      scale: 0.5,
      // XR8 reports a unit-height plane whose width follows the target image aspect.
      scaledWidth: properties.width / properties.height,
      scaledHeight: 1,
    };
    current.testApi = {
      get targetsReady() { return current.targetsReady; },
      async ready() { await current.presentationReady; },
      setBackground(hidden) {
        windowRef.__browserArVisibilityHidden = Boolean(hidden);
        documentRef.dispatchEvent(new Event('visibilitychange'));
      },
      targetFound() { onTarget(current, syntheticDetail, true, emit); },
      targetLost() { onTarget(current, syntheticDetail, false, emit); },
      updateVideoSources(urlsByVideoId) {
        return current.presentation.updateVideoSources(new Map(Object.entries(urlsByVideoId)));
      },
      measurementSnapshot() { return current.measurements.snapshot(); },
      presentationSnapshot() {
        const video = current.presentation?.videoElements[0]?.element;
        const player = current.presentation?.snapshot()[0];
        const targetSnapshot = current.presentation?.targetSnapshot(target.id);
        let bufferedAheadSeconds = 0;
        if (video?.buffered && Number.isFinite(video.currentTime)) {
          for (let index = 0; index < video.buffered.length; index++) {
            const rangeStart = video.buffered.start(index);
            const rangeEnd = video.buffered.end(index);
            if (rangeStart <= video.currentTime && video.currentTime <= rangeEnd) {
              bufferedAheadSeconds = Math.max(0, rangeEnd - video.currentTime);
              break;
            }
          }
        }
        return {
          visible: targetSnapshot?.visible ?? false,
          currentTime: video?.currentTime ?? 0,
          duration: video?.duration ?? 0,
          paused: video?.paused ?? true,
          seeking: video?.seeking ?? false,
          blocked: player?.blocked ?? false,
          readyState: video?.readyState ?? 0,
          networkState: video?.networkState ?? 0,
          bufferedAheadSeconds,
          videoWidth: video?.videoWidth ?? 0,
          videoHeight: video?.videoHeight ?? 0,
          mediaErrorCode: video?.error?.code ?? null,
          loop: video?.loop ?? false,
          sourceKind: media.sourceKind,
          targetImageAspect: properties.width / properties.height,
          sourceImageBounds: targetSnapshot?.bounds ?? null,
          planeScale: targetSnapshot?.planeScale ?? null,
          planePosition: targetSnapshot?.planePosition ?? null,
          selectedTargetId: player?.selectedTargetId ?? null,
          renewalCount: player?.renewalCount ?? 0,
        };
      },
    };
    windowRef.__browserArTest = current.testApi;
  }
}

async function loadBrowserTestVideoFixture(documentRef, windowRef) {
  const sourceUrl = windowRef.__browserArTestVideoUrl;
  if (typeof sourceUrl !== 'string' || sourceUrl.length === 0) {
    throw new Error('Browser test needs a configured, decodable video fixture.');
  }

  const fixturePath = new URL(sourceUrl, windowRef.location.href).pathname;
  const isSyntheticFixture = fixturePath.endsWith('/synthetic-ar-playback.webm');
  const sourceKind = isSyntheticFixture
    ? 'generated-synthetic-webm-fixture'
    : 'verified-private-kimtech-mp4';
  const video = documentRef.createElement('video');
  video.crossOrigin = 'anonymous';
  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';
  video.src = sourceUrl;
  await new Promise((resolve, reject) => {
    video.addEventListener('loadedmetadata', resolve, {once: true});
    video.addEventListener('error', () => reject(new Error('Configured browser test video could not be decoded.')), {once: true});
    video.load();
  });
  if (!Number.isFinite(video.duration) || video.duration <= 1) {
    throw new Error(`Configured browser test video must exceed one second (duration ${video.duration}).`);
  }
  return {
    video,
    sourceKind,
    cleanup() {
      video.pause();
      video.removeAttribute('src');
      video.load();
    },
  };
}

function pixelRatio() { return 1; }

async function waitForCanvas(documentRef, windowRef, isCurrent) {
  const maximumFrames = 120;
  for (let frame = 0; frame < maximumFrames; frame++) {
    if (!isCurrent()) return null;
    const canvas = documentRef.getElementById('ar-canvas');
    if (canvas) return canvas;
    await new Promise(resolve => {
      if (typeof windowRef.requestAnimationFrame === 'function') {
        windowRef.requestAnimationFrame(resolve);
      } else {
        setTimeout(resolve, 16);
      }
    });
  }
  if (!isCurrent()) return null;
  return documentRef.getElementById('ar-canvas');
}

async function mapConcurrent(items, concurrency, mapper) {
  const results = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (true) {
      const index = next++;
      if (index >= items.length) return;
      results[index] = await mapper(items[index], index);
    }
  };
  await Promise.all(Array.from({length: Math.min(concurrency, items.length)}, worker));
  return results;
}

function safeMessage(error) {
  return typeof error?.message === 'string' && error.message.length < 180
    ? error.message
    : 'The camera session could not continue. Close and try again.';
}
